import {
  CircularProgress,
  Icon,
  InteractableComponent,
  Text,
} from '@shared/components';
import { InteractableComponentProps } from '@shared/components/interactable-component/InteractableComponent';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import clsx from 'clsx';
import { format } from 'date-fns';
import { ReactElement } from 'react';
import {
  svgBlank,
  svgDownload,
  svgGIF,
  svgJPG,
  svgMOV,
  svgMP3,
  svgPDF,
  svgPNG,
  svgRetry,
  svgTrash,
  svgTXT,
  svgXLS,
} from './svgs';

export type AttachmentPreviewImageProps = {
  alt: string;
  height: number;
  src: string;
  width: number;
};

export type AttachmentProps = StyleProps & {
  /**
   * The mime type of the attachment. Used to show the right logo.
   * You can pass any other file type, but only some are support with logos.
   * @see https://www.sitepoint.com/mime-types-complete-list/
   * @default 'application/pdf'
   */
  filetype?:
    | 'application/pdf'
    | 'image/jpeg'
    | 'image/gif'
    | 'image/png'
    | 'text/plain'
    | 'application/excel'
    | 'video/quicktime'
    | 'audio/mpeg3'
    | string;
  /** The name of the attachment */
  name: string;
  /** Date to display under the name */
  date?: Date;
  /** The size of the file to display under the name */
  size?: string;
  /**
   * If there's an error the message of the error to display.
   */
  errorMsg?: string;
  /**
   * If the attachment is uploading.
   */
  isUploading?: boolean;
  /**
   * If progress of the upload is known, then the progress.
   */
  uploadProgress?: number;
  /** If the  */
  previewImgSrc?: string;
  /** Whether the attachment has a remove button */
  hasRemoveButton?: boolean;
  /**
   * A presigned download url for the document from S3.
   * Used for download button.
   */
  downloadUrl?: string;
  /** Handler to call if user clicks remove button */
  onRemove?: () => void;
  /** Handler to call if user clicks cancel buton while uploading */
  onCancelUpload?: () => void;
  /** Handler to call if users clicks retry upload for the file */
  onRetryUpload?: () => void;
  onPress?: () => void;
  renderPreviewImage?: (props: AttachmentPreviewImageProps) => ReactElement;
};

const ROOT = makeRootClassName('Attachment');
const el = makeElementClassNameFactory(ROOT);

const DEFAULT_PROPS = {
  size: '',
} as const;

const getIconForFileType = (
  filetype: AttachmentProps['filetype']
): ReactElement => {
  switch (filetype) {
    case 'application/pdf':
      return svgPDF;
    case 'image/jpeg':
      return svgJPG;
    case 'image/gif':
      return svgGIF;
    case 'image/png':
      return svgPNG;
    case 'text/plain':
      return svgTXT;
    case 'application/excel':
    case 'application/vnd.ms-excel':
    case 'application/vnd.ms-excel.sheet.macroEnabled.12':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return svgXLS;
    case 'video/quicktime':
      return svgMOV;
    case 'audio/mpeg3':
      return svgMP3;
    default:
      return svgBlank;
  }
};

type InteractiveProps = Omit<InteractableComponentProps, 'asChild'>;

const IconButton = (props: InteractiveProps): ReactElement => (
  <InteractableComponent {...props} asChild>
    {props.children}
  </InteractableComponent>
);

export function Attachment(props: AttachmentProps): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };

  const isUploaded = !p.isUploading && !p.errorMsg;
  const hasBothDateSize = p.date && p.size;
  const displayDate = p.date ? format(p.date, 'dd MMM, yyyy') : '';
  const displayDateSize = `${displayDate}${hasBothDateSize ? ' • ' : ''}${
    p.size
  }`;
  const showDateSize = (p.date || p.size) && !p.errorMsg;

  return (
    <InteractableComponent
      onPress={p.onPress}
      className={clsx(ROOT, p.className)}
    >
      <>
        <div className={el`left`}>
          {p.previewImgSrc &&
            (p.renderPreviewImage ? (
              p.renderPreviewImage({
                alt: p.name,
                height: 64,
                src: p.previewImgSrc,
                width: 52,
              })
            ) : (
              <img src={p.previewImgSrc} width={52} height={64} alt={p.name} />
            ))}
          {!p.previewImgSrc && (
            <Icon
              viewBoxHeight={24}
              viewBoxWidth={24}
              content={getIconForFileType(p.filetype)}
            />
          )}
          <div className={el`name-date-size`}>
            <Text type="body-sm" isHeavy className="text-gray-700">
              {p.name}
            </Text>
            {p.errorMsg && (
              <Text type="body-xs" className="text-red-500">
                {p.errorMsg}
              </Text>
            )}
            {showDateSize && (p.date || p.size) && (
              <Text type="body-xs" className="text-gray-500">
                {displayDateSize}
              </Text>
            )}
          </div>
        </div>
        <div className={el`actions`}>
          {p.isUploading && (
            <CircularProgress variant="indeterminate" size="xs" />
          )}
          {p.errorMsg && !p.isUploading && (
            <IconButton onPress={p.onRetryUpload}>{svgRetry}</IconButton>
          )}
          {!p.isUploading && p.hasRemoveButton && (
            <IconButton
              onPress={p.onRemove}
              className="cursor-pointer text-red-500"
            >
              {svgTrash}
            </IconButton>
          )}
          {isUploaded && p.downloadUrl && (
            <a href={p.downloadUrl} target="_blank" rel="noreferrer">
              <IconButton>{svgDownload}</IconButton>
            </a>
          )}
        </div>
      </>
    </InteractableComponent>
  );
}

export default Attachment;
